import { ReactNode } from "react";

export type Props = {
  /** Additional CSS classes */
  className?: string;
  /** Additional CSS classes for the spinner element */
  spinnerClassName?: string;
  /** Custom spinner renderer */
  renderSpinner?: () => ReactNode;
  /** Accessibility label for the loader */
  "aria-label"?: string;
  /** Test ID for testing */
  "data-testid"?: string;
};

/**
 * Base loader/spinner component with no styling.
 * Use renderSpinner to customize the spinner appearance.
 *
 * @example
 * // In shared/ui/Loader.tsx
 * import { LoaderBase, type Props as LoaderBaseProps } from "@core/ui";
 *
 * export function Loader({ className, ...props }: LoaderBaseProps) {
 *   return (
 *     <LoaderBase
 *       className={twMerge("flex items-center justify-center", className)}
 *       renderSpinner={() => (
 *         <svg className="animate-spin h-8 w-8" viewBox="0 0 24 24">
 *           <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
 *         </svg>
 *       )}
 *       {...props}
 *     />
 *   );
 * }
 */
export function LoaderBase({
  className,
  spinnerClassName,
  renderSpinner,
  "aria-label": ariaLabel = "Loading",
  "data-testid": testId = "loader",
}: Props) {
  // Default spinner is a simple text indicator
  const defaultSpinner = (
    <span className={spinnerClassName} data-loader-spinner>
      Loading...
    </span>
  );

  return (
    <div
      role="status"
      aria-label={ariaLabel}
      aria-busy="true"
      className={className}
      data-testid={testId}
    >
      {renderSpinner ? renderSpinner() : defaultSpinner}
      {/* Screen reader only text */}
      <span
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: "hidden",
          clip: "rect(0, 0, 0, 0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      >
        {ariaLabel}
      </span>
    </div>
  );
}

LoaderBase.displayName = "LoaderBase";


